import { type FC, useMemo } from 'react'
import toast from 'react-hot-toast'
import { Chip } from '@/components/commons/chip'
import { PlusIcon, XCloseIcon } from '@/components/ui/icons'
import { FlagsPopover } from '@/features/photoset/components/flags-popover/flags-popover'
import {
	type RemoveImagesFlagData,
	useRemoveImagesFlag
} from '@/features/photoset/queries/use-remove-images-flag'
import type {
	Flag,
	Image,
	Photoset,
	PhotosetSpace
} from '@/features/photoset/types'
import { useTranslation } from '@/translations/i18n'

interface ImageCardFlagsProps {
	image: Omit<Image, 'space'>
	stepId: Photoset['step_id']
	photosetSpaceIds?: PhotosetSpace['id'][]
	types?: Flag['type'][]
	hideFlagsDescriptions?: boolean
	onRemoveFlagCb?: (data: RemoveImagesFlagData) => void
	isReadOnly?: boolean
}

export const ImageCardFlags: FC<ImageCardFlagsProps> = ({
	image,
	stepId,
	types,
	photosetSpaceIds,
	hideFlagsDescriptions = false,
	isReadOnly = false,
	onRemoveFlagCb
}) => {
	const { t } = useTranslation()
	const removeImagesFlag = useRemoveImagesFlag({
		stepId,
		photosetSpaceIds,
		options: {
			onError: () => toast.error(t('errors:default')),
			onSuccess: onRemoveFlagCb
		}
	})

	const flags = useMemo(() => {
		if (!image.flags) {
			return []
		}
		if (!types) {
			return image.flags
		}

		return image.flags.filter((flag) => types.includes(flag.type))
	}, [image.flags, types])

	return (
		<div className='flex w-full flex-wrap gap-1.5'>
			{flags.map((flag) => (
				<Chip
					endSlot={
						!isReadOnly && (
							<button
								onClick={() => {
									removeImagesFlag.mutate({
										flag,
										imageIds: [image.id]
									})
								}}
								tabIndex={-1}
								type='button'
							>
								<XCloseIcon />
							</button>
						)
					}
					isActive
					key={flag.id}
					label={t(`photoset:flags.${flag.key_name}.label`)}
					variant='info'
				/>
			))}

			{!isReadOnly && (
				<FlagsPopover
					disableScrollBelow
					hideDescriptions={hideFlagsDescriptions}
					onRemoveFlagCb={onRemoveFlagCb}
					photosetSpaceIds={photosetSpaceIds}
					selectedImagesIds={[image.id]}
					stepId={stepId}
					trigger={
						<Chip
							endSlot={<PlusIcon />}
							isActive={false}
							label={t('common:flag', { count: 1 })}
							variant='info'
						/>
					}
					types={types}
				/>
			)}
		</div>
	)
}
